import { Injectable } from '@angular/core';
import { BaseDataService } from './base.data.service';
import { HttpClient } from '@angular/common/http';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { IVehicleLifeCycleStatusFilter } from '../interfaces/vehicle-life-cycle/vehicle-life-cycle-status-filter';
import { IVehicleLifeCycleNewOrderDetails } from '../interfaces/vehicle-life-cycle/vehicle-life-cycle-new-order-details';
import { IVehicleLifeCycleAdditionalOrderInfo } from '../interfaces/vehicle-life-cycle/vehicle-life-cycle-additional-order-info';
import { IVehicleLifeCycleProvider } from '../interfaces/vehicle-life-cycle/vehicle-life-cycle-provider';
import { IVehicleLifeCycleProviderConnection } from '../interfaces/vehicle-life-cycle/vehicle-life-cycle-provider-connection';
import { IVehicleLifeCycleTeamConnection } from '../interfaces/vehicle-life-cycle/vehicle-life-cycle-team-connection';
import { IVehicleLifeCycleCustomerSiteConnection } from '../interfaces/vehicle-life-cycle/vehicle-life-cycle-customer-site-connection';
import { IVehicleLifeCycleSiteStatus } from '../interfaces/vehicle-life-cycle/vehicle-life-cycle-site-status';
import { IVehicleLifeCycleSiteDetails } from '../interfaces/vehicle-life-cycle/vehicle-life-cycle-site-details';

@Injectable({
  providedIn: 'root',
})
export class VehicleLifecycleService extends BaseDataService {
  public url = '/api/vehiclelifecycle';

  constructor(public http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
    super(http, handlersFactory);
  }

  public getOverviewCounts(): Promise<any> {
    return this.http.get<any>(`${this.url}/overviewCounts`).toPromise();
  }

  public getVehicleStatusDetails(filterCommand: IVehicleLifeCycleStatusFilter): Promise<any> {
    return this.http
      .get<any>(`${this.url}/getStatusDetails`, {
        params: this.constructHttpParams(filterCommand),
      })
      .toPromise();
  }

  public getTeams(): Promise<any> {
    return this.http.get<any>(`${this.url}/teams`).toPromise();
  }

  public getVehicleMakes(): Promise<any> {
    return this.http.get<any>(`${this.url}/vehicleMakes`).toPromise();
  }

  public getVehicleModels(vehicleMakes: string[]): Promise<any> {
    return this.http.post<any>(`${this.url}/vehicleModels`, vehicleMakes).toPromise();
  }

  public getRefurbInfoForNewOrderCreation(
    refurbId: number,
  ): Promise<IVehicleLifeCycleNewOrderDetails> {
    return this.http
      .get<IVehicleLifeCycleNewOrderDetails>(
        `${this.url}/refurbDetailsForNewOrder/${encodeURIComponent(refurbId)}`,
      )
      .toPromise();
  }

  public getAdditionalOrderInfo(taskId: number): Promise<IVehicleLifeCycleAdditionalOrderInfo> {
    return this.http
      .get<IVehicleLifeCycleAdditionalOrderInfo>(
        `${this.url}/additionalOrderInformation?taskId=${encodeURIComponent(taskId)}`,
      )
      .toPromise();
  }

  public downloadReport(filterCommand: IVehicleLifeCycleStatusFilter) {
    let params = this.constructHttpParams(filterCommand);
    return this.http.get(this.url + '/export', { params: params, responseType: 'blob' });
  }

  public getProviders(): Promise<IVehicleLifeCycleProvider[]> {
    return this.http.get<IVehicleLifeCycleProvider[]>(`${this.url}/providers`).toPromise();
  }

  public getProviderConnections(orgId: number): Promise<IVehicleLifeCycleProviderConnection[]> {
    return this.http
      .get<IVehicleLifeCycleProviderConnection[]>(
        `${this.url}/organisation/${encodeURIComponent(orgId)}/providerConnections`,
      )
      .toPromise();
  }

  public getTeamProviderConnections(teamId: number): Promise<IVehicleLifeCycleTeamConnection[]> {
    return this.http
      .get<IVehicleLifeCycleTeamConnection[]>(
        `${this.url}/teams/${encodeURIComponent(teamId)}/providerConnections`,
      )
      .toPromise();
  }

  public getCustomerSitesProviderConnections(
    siteId: number,
  ): Promise<IVehicleLifeCycleCustomerSiteConnection[]> {
    return this.http
      .get<IVehicleLifeCycleCustomerSiteConnection[]>(
        `${this.url}/sites/${encodeURIComponent(siteId)}/providerConnections`,
      )
      .toPromise();
  }

  public addUpdateProviderConnection(form: any): Promise<IVehicleLifeCycleProviderConnection[]> {
    return this.http
      .post<IVehicleLifeCycleProviderConnection[]>(
        `${this.url}/organisation/addUpdateProviderConnection`,
        form,
      )
      .toPromise();
  }

  public addUpdateTeamProviderConnection(form: any): Promise<IVehicleLifeCycleTeamConnection[]> {
    return this.http
      .post<IVehicleLifeCycleTeamConnection[]>(`${this.url}/team/addUpdateProviderConnection`, form)
      .toPromise();
  }

  public addUpdateCustomerSiteProviderConnection(
    form: any,
  ): Promise<IVehicleLifeCycleCustomerSiteConnection[]> {
    return this.http
      .post<IVehicleLifeCycleCustomerSiteConnection[]>(
        `${this.url}/customerSite/addUpdateProviderConnection`,
        form,
      )
      .toPromise();
  }

  public getActiveCustomerSites(): Promise<any> {
    return this.http.get<any>(`${this.url}/activeSites`).toPromise();
  }

  public getSiteStatusOverview(): Promise<IVehicleLifeCycleSiteStatus[]> {
    return this.http.get<any>(`${this.url}/siteOverview`).toPromise();
  }

  public getSiteDetails(): Promise<IVehicleLifeCycleSiteDetails[]> {
    return this.http.get<any>(`${this.url}/siteDetails`).toPromise();
  }
}
